// The renderer's cost knobs (crossScene.setQuality), as data: what each one
// is, its range, and its default. No DOM, no three.js: validation is pure so
// node tests can check it, and the quality lab (?tune) builds its controls
// from these specs.
//
// The defaults are the site's look; every knob at its default renders
// pixel-identically to a scene that never heard of them. Each knob was kept
// because it changes GPU cost in this pipeline (perf/sweep.mjs measures by how
// much), or (marked `look`, costing nothing) because it makes what a cost knob
// buys easier to see: exaggerated, the effect shows plainly in the lab. The
// material's other visual settings (colors, envMapIntensity) are not here.
//
// The custom glass (?glass=custom, js/glass/) takes the same knobs except
// samples (it takes one lookup per pixel). Its look is its own (js/glass/
// look.js): dispersionStrength and cavityStrength, the MTM glass's look
// knobs, don't touch it; dispersion and the maps compile its lookups in or
// out; and having no live noise, it reads the baked distortion field for
// distortionMode live as for baked.
//
// `apply` says what a change costs at the moment it is made:
//   "runtime"  — a uniform or a loop parameter; free
//   "resize"   — reallocates buffers (the canvas: a black flash unless the
//                page redraws in the same task)
//   "compile"  — a different shader program; the first switch to a new
//                combination compiles it (a stall of up to ~100 ms), after
//                that switching back and forth is instant (programs are cached)

export const QUALITY_KNOBS = Object.freeze({
  renderScale: {
    kind: "number", min: 0.25, max: 1, step: 0.05, default: 1, apply: "resize",
    label: "Canvas resolution",
    doc: "multiplies the canvas's pixel ratio; the transmission buffers keep theirs",
  },
  transmissionScale: {
    kind: "number", min: 0.15, max: 1, step: 0.05, default: 0.55, apply: "resize",
    label: "Transmission buffer scale",
    doc: "the two refraction buffers' size relative to the page's transmission pixel ratio",
  },
  samples: {
    kind: "int", min: 1, max: 12, step: 1, default: 6, apply: "compile",
    label: "Refraction samples",
    doc: "MeshTransmissionMaterial's jittered refraction samples per pixel, in the back and front glass passes",
  },
  dispersion: {
    kind: "bool", default: true, apply: "compile",
    label: "Dispersion",
    doc: "three buffer lookups per sample (one per color channel, each refracted with its own IOR) instead of one; off = no color fringes",
  },
  dispersionStrength: {
    kind: "number", min: 0, max: 0.1, step: 0.001, default: 0.01, apply: "runtime", look: true,
    label: "Dispersion strength",
    doc: "the chromatic aberration: how much further green and blue refract than red (the IOR spread across a pixel's samples); no effect with dispersion off",
  },
  distortionMode: {
    kind: "enum", values: ["live", "baked", "off"], default: "live", apply: "compile",
    label: "Distortion noise",
    doc: "the printed-resin refraction wobble. live: four octaves of 3D simplex noise, three times per glass pixel, fixed in the world (the cross turns through it); baked: the same noise computed once into a 3D texture fixed to the cross, one lookup per pixel (js/distortion-field.js); off: none (a uniform: no compile)",
  },
  orangePeel: {
    kind: "bool", default: true, apply: "compile",
    label: "Orange-peel normal map",
    doc: "the procedural surface micro-texture (layer lines and grain)",
  },
  ao: {
    kind: "bool", default: true, apply: "compile",
    label: "AO map",
    doc: "the baked ambient occlusion (assets/ao.png)",
  },
  cavity: {
    kind: "bool", default: true, apply: "compile",
    label: "Cavity map",
    doc: "the baked groove shading tinted into the surface color (assets/cavity.png)",
  },
  cavityStrength: {
    kind: "number", min: 0, max: 3, step: 0.05, default: 1, apply: "runtime", look: true,
    label: "Cavity strength",
    doc: "the groove darkening, as a multiple of the bake's (cavity.png: 0.82 + 0.18 × AO; 0 = none, 3 = three times as dark); no effect with the cavity map off",
  },
  halfFloat: {
    kind: "bool", default: true, apply: "resize",
    label: "Half-float buffers",
    doc: "the transmission buffers as 16-bit floats; off = 8-bit (cheaper bandwidth, may band)",
  },
  transmissionInterval: {
    kind: "int", min: 1, max: 4, step: 1, default: 1, apply: "runtime",
    label: "Refresh while moving",
    doc: "while the pose moves, the transmission passes (scene + back faces) refresh every Nth frame (1: every frame, the default); the page's render loop reads it",
  },
});

export const QUALITY_DEFAULTS = Object.freeze(
  Object.fromEntries(Object.entries(QUALITY_KNOBS).map(([key, spec]) => [key, spec.default])),
);

// `current` with `partial` applied, frozen. Throws on an unknown knob or a
// value outside its spec: a typo in a knob name must not silently do nothing.
export function resolveQuality(current, partial) {
  if (partial === null || typeof partial !== "object") throw new TypeError(`quality: expected an object, got ${partial}`);
  const next = { ...current };
  for (const [key, value] of Object.entries(partial)) {
    const spec = QUALITY_KNOBS[key];
    if (!spec) throw new TypeError(`quality: unknown knob "${key}"; expected one of ${Object.keys(QUALITY_KNOBS).join(", ")}`);
    if (spec.kind === "bool") {
      if (typeof value !== "boolean") throw new TypeError(`quality: ${key} must be a boolean, got ${value}`);
    } else if (spec.kind === "enum") {
      if (!spec.values.includes(value)) throw new RangeError(`quality: ${key} must be one of ${spec.values.join(", ")}, got ${value}`);
    } else {
      if (typeof value !== "number" || !(value >= spec.min && value <= spec.max)) {
        throw new RangeError(`quality: ${key} must be a number in ${spec.min}..${spec.max}, got ${value}`);
      }
      if (spec.kind === "int" && !Number.isInteger(value)) throw new RangeError(`quality: ${key} must be an integer, got ${value}`);
    }
    next[key] = value;
  }
  return Object.freeze(next);
}

// The knobs that differ from the defaults, as "key:value" pairs joined by
// commas (booleans as 1/0, enums by name) — the quality lab's URL form. parseQuality inverts it.
export function formatQuality(quality) {
  return Object.entries(quality)
    .filter(([key, value]) => value !== QUALITY_DEFAULTS[key])
    .map(([key, value]) => `${key}:${typeof value === "boolean" ? Number(value) : value}`)
    .join(",");
}

export function parseQuality(text) {
  const partial = {};
  for (const pair of text.split(",").filter(Boolean)) {
    const [key, raw, extra] = pair.split(":");
    const spec = QUALITY_KNOBS[key];
    if (!spec || raw === undefined || extra !== undefined) throw new TypeError(`quality: cannot read "${pair}"; expected knob:value`);
    if (spec.kind === "bool") {
      if (raw !== "0" && raw !== "1") throw new TypeError(`quality: ${key} must be 0 or 1, got "${raw}"`);
      partial[key] = raw === "1";
    } else if (spec.kind === "enum") {
      partial[key] = raw;
    } else {
      partial[key] = Number(raw);
    }
  }
  return resolveQuality(QUALITY_DEFAULTS, partial);
}
